import React, { useEffect, useState, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

const speak = (text, rate = 0.9) => {
  const synth = window.speechSynthesis;
  synth.cancel(); 
  const voices = synth.getVoices();
  const femaleVoice =
    voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") ||
        v.name.toLowerCase().includes("anya") ||
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("susan"))
    ) || voices.find(v => v.lang === "en-US");

  const utter = new SpeechSynthesisUtterance(text);
  utter.voice = femaleVoice;
  utter.lang = "en-US";
  utter.rate = rate;
  synth.speak(utter);
};

if (window.speechSynthesis.onvoiceschanged !== undefined) {
  window.speechSynthesis.onvoiceschanged = () => {
    window.speechSynthesis.getVoices();
  };
}

// Add the ConfirmToast component
const ConfirmToast = ({ message, onConfirm, onCancel }) => {
  return (
    <div style={{ maxWidth: '600px' }}>
      <div className="mb-3">
        <h6 className="mb-0 text-center meassage-text">{message}</h6>
      </div>
      <div className="d-flex justify-content-center gap-2">
        <Button className='yes-button' size="sm" onClick={onConfirm}>
          Yes
        </Button>
        <Button className='No-button' size="sm" onClick={onCancel}>
          No
        </Button>
      </div>
    </div>
  );
};

const Role = () => {
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({ roleName: '' });
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef();
  const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] = useState(false);

  const BASE_ENDPOINT = '/UserRole';
  const ALLOWED_ROLES = ['SuperAdmin', 'admin', 'viewer', 'taskcoordinator'];

  useEffect(() => {
    const timer = setTimeout(() => {
      speak("Welcome to Role Management. You can add, edit or delete roles here.");
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiGet(`${BASE_ENDPOINT}/GetAll`);
      const sortedRoles = response?.data?.sort((a, b) => b.roleID - a.roleID) || [];
      setRoles(sortedRoles);
    } catch (error) {
      logger.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
      speak("Failed to load roles. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus();
      speak(editId ? "Editing role. " : "Adding new role.");
    }
  }, [isModalOpen, editId]);

  const openModal = (data = { roleName: '' }) => {
    setFormData(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setFormData({ roleName: '' });
    setEditId(null);
    setIsModalOpen(false);
    speak("Form closed");
  };

  const sanitizeRoleName = (input) => {
    return input
      .replace(/\./g, '') 
      .replace(/\s+/g, ' ') 
      .trim();
  };

  const formatRoleName = (input) => {
    return input.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleSpeechInput = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      speak("Listening...");
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      speak("Sorry, I didn't catch that. Please try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();

      transcript = formatRoleName(sanitizeRoleName(transcript));
      setFormData({ roleName: transcript });
      speak(`You said: ${transcript}`);
    };

    recognition.start();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let trimmedName = sanitizeRoleName(formData.roleName?.trim());
    trimmedName = formatRoleName(trimmedName);

    if (!trimmedName) {
      const msg = 'Role name is required!';
      toast.error(msg);
      speak(msg);
      return;
    }

    const lowerName = trimmedName.toLowerCase();
    // if (!ALLOWED_ROLES.includes(lowerName)) {
    //   const msg = 'Only SuperAdmin, Admin, Viewer, and TaskCoordinator roles are allowed!';
    //   toast.error(msg);
    //   speak(msg);
    //   return;
    // }

    if (!editId && roles.length >= 4) {
      const msg = 'You can only add up to 4 roles!';
      toast.error(msg);
      speak(msg);
      return;
    }

    try {
      setIsLoading(true);
      const payload = { roleName: trimmedName };

      if (editId) {
        await apiPut(`${BASE_ENDPOINT}/Update`, { ...payload, roleID: editId });
        const msg = `Role Updated Successfully!`;
        toast.success(msg);
        speak(msg);
      } else {
        await apiPost(`${BASE_ENDPOINT}/Create`, payload);
        const msg = `Role Added Successfully! `;
        toast.success(msg);
        speak(msg);
      }

      await fetchRoles();
      closeModal();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiError = (error) => {
    logger.error('API Error:', error);
    const statusCode = error?.response?.status;
    const apiMessage = error?.response?.data?.status;

    const messages = {
      400: apiMessage || 'Invalid role data',
      404: 'Role not found',
      409: 'Role name already exists',
      500: 'Server error'
    };

    const errorMessage = messages[statusCode] || 'Something went wrong';
    const fullMessage = `Operation Failed. ${errorMessage}`;
    
    toast.error(fullMessage);
    speak(fullMessage);
  };

  const handleEdit = (role) => {
    setEditId(role.roleID);
    openModal({ roleName: role.roleName });
  };

  const handleDelete = (id) => {
    if (isDeleteConfirmationActive) return;
    const roleToDelete = roles.find(r => r.roleID === id);
    const confirmMsg = `Are you sure you want to delete the role: ${roleToDelete?.roleName}?`;
    
    setIsDeleteConfirmationActive(true);
    speak(confirmMsg);

    toast.info(
      <ConfirmToast
        message={confirmMsg}
        onConfirm={async () => {
          toast.dismiss();
          try {
            setIsLoading(true);
            await apiDelete(`${BASE_ENDPOINT}/Delete/${id}`);
            const msg = 'Role Deleted Successfully!';
            toast.success(<strong>{msg}</strong>, {
              autoClose: 5000,
              icon: false,
            });
            speak(msg);
            await fetchRoles();
          } catch (error) {
            handleApiError(error);
          } finally {
            setIsLoading(false);
            setIsDeleteConfirmationActive(false);
          }
        }}
        onCancel={() => {
          toast.dismiss();
          speak("Delete cancelled.");
          setIsDeleteConfirmationActive(false);
        }}
      />,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
        icon: false,
        className: 'custom-toast',
        bodyClassName: 'custom-toast-body',
        hideProgressBar: true,
      }
    );
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.roleID,
      sortable: true,
      omit: true,
    },
    {
      name: 'Role Name',
      selector: row => row.roleName,
      sortable: true,
      width: '600px',
      center: true,
      cell: row => <span className="text-capitalize">{row.roleName}</span>,
      style: {
        borderRight: '1px solid #e5e8ebff!important', 
      },
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2">
          <Button 
            variant="outline-warning custompadding" 
            size="sm me-2" 
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            variant="outline-danger custompadding" 
            size="sm me-2" 
            onClick={() => handleDelete(row.roleID)}
            disabled={isDeleteConfirmationActive}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      ),
      width: '450px',
      center: true,
    }
  ];

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 page-heading">Role Management</h2>
        <Button
          variant="btn-primary"
          className="btn-fontweight"
          onClick={() => openModal()}
          disabled={isLoading || roles.length >= 4}
        >
          {isLoading ? <Spinner size="sm" /> : 'Add Role'}
        </Button>
      </div>

      {isLoading && !roles.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="btn-primary" />
          <p className="mt-2">Loading roles...</p>
        </div>
      ) : (
        <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white">
          <DataTable
            columns={columns}
            data={roles.map((dept, idx) => ({
              ...dept,
              _rowIndex: idx, 
            }))}
            pagination
            className="custom-data-table department-table"
            customStyles={{
              table: {
                style: {
                  borderRight: '1px solid #dee2e6',
                  fontWeight: '600',
                  fontSize: '14px',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "uppercase",
                },
              },
              rows: {
                style: {
                  borderBottom: "1px solid #fdfdfd",
                  "&:hover": {
                    backgroundColor: "#fdfdfd",
                  },
                },
              },
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
            conditionalRowStyles={[
              {
                when: (row) => row._rowIndex % 2 === 0, 
                style: { backgroundColor: '#f2f2f2' },
              },
              {
                when: (row) => row._rowIndex % 2 !== 0,
                style: { backgroundColor: '#ffffff' },
              },
            ]}
            striped
            responsive
            noDataComponent={<Alert variant="info">No roles found</Alert>}
            progressPending={isLoading}
            progressComponent={<Spinner animation="border" variant="btn-primary" />}
            onRowClicked={(row) => speak(`Role: ${row.roleName}`)}
          />
        </div>
      )}

      <Modal show={isModalOpen} onHide={closeModal} backdrop="static">
        <Modal.Header closeButton className="modal-customheader">
          <Modal.Title className="custom-modaltitle">{editId ? 'Edit Role' : 'Add Role'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="custommodal-body">
            <Form.Group>
              <Form.Label className='mb-2'>Role Name</Form.Label>
              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  ref={inputRef}
                  value={formData.roleName}
                  onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                  placeholder="Enter role name"
                  required
                  disabled={isLoading}
                />
                <Button 
                  variant={isListening ? "danger" : ""} 
                  onClick={handleSpeechInput}
                  disabled={isLoading}
                  className='addcategory-button'
                >
                  <i className={`bi bi-mic${isListening ? "-fill" : ""}`}></i>
                </Button>
              </div>
              <Form.Text className="text-muted mar-top6">
                Allowed: SuperAdmin, Admin, Viewer, TaskCoordinator
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="custommodal-footer pb-3">
            <Button 
              variant="secondary" 
              className="btn-fontweight cancelbtn-fontsize" 
              onClick={closeModal} 
              disabled={isLoading}
              onFocus={() => speak("Cancel button")}
            >
              Cancel
            </Button>
            <Button 
              variant="btn-primary" 
              className="btn-fontweight addbtn-fontsize" 
              type="submit" 
              disabled={isLoading}
              onFocus={() => speak(editId ? "Update role button" : "Add role button")}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {editId ? 'Updating...' : 'Adding...'}
                </>
              ) : editId ? 'Update Role' : 'Add Role'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Role;